import { Bell, Smartphone } from 'lucide-react';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useShop } from '../../../shared/store';

type AppPlatform = 'android' | 'ios';

type PlatformContent = {
  platform: AppPlatform;
  badgeLabel: string;
  storeLabel: string;
  title: string;
  accentClass: string;
  accentGlowClass: string;
  chipClass: string;
  summary: string;
};

const GooglePlayBadgeIcon = () => (
  <svg viewBox="0 0 48 48" className="h-12 w-12" aria-hidden="true" focusable="false">
    <path d="M8 7.5L26.5 26 8 44.5z" fill="#00d2ff" />
    <path d="M8 7.5l23 13.5-4.5 5L8 7.5z" fill="#00f076" />
    <path d="M8 44.5l18.5-18.5 4.5 5L8 44.5z" fill="#ff5252" />
    <path d="M31 21L38.8 25.6c1.6.95 1.6 3.25 0 4.2L31 34.5 22 25.5z" fill="#ffcc00" />
  </svg>
);

const AppleBadgeIcon = () => (
  <svg viewBox="0 0 16 16" className="h-7 w-7 fill-current text-slate-950" aria-hidden="true" focusable="false">
    <path d="M11.182 0c.051.79-.216 1.386-.606 1.84-.396.466-1.044.826-1.684.776-.08-.729.242-1.399.576-1.79C9.862.35 10.531.038 11.18 0h.003zM13.56 12.153c-.298.687-.44.994-.824 1.595-.536.857-1.294 1.925-2.234 1.933-.836.008-1.05-.551-2.184-.542-1.135.005-1.37.553-2.207.545-.94-.008-1.656-.97-2.192-1.827C2.417 11.463 2.262 8.653 3.186 7.221c.655-1.026 1.694-1.625 2.67-1.625.995 0 1.622.56 2.446.56.801 0 1.29-.56 2.442-.56.87 0 1.79.48 2.443 1.31-2.138 1.19-1.792 4.275.373 5.247z" />
  </svg>
);

const platformContent: Record<AppPlatform, PlatformContent> = {
  android: {
    platform: 'android',
    badgeLabel: 'GET IT ON',
    storeLabel: 'Google Play',
    title: 'Android app is coming soon',
    accentClass: 'from-orange-100 via-white to-teal-100',
    accentGlowClass: 'bg-orange-400/20',
    chipClass: 'bg-orange-500 text-white',
    summary: 'Launching soon on Google Play with books, audio, and animation in one mobile experience.',
  },
  ios: {
    platform: 'ios',
    badgeLabel: 'Download on the',
    storeLabel: 'App Store',
    title: 'iPhone app is coming soon',
    accentClass: 'from-sky-100 via-white to-emerald-100',
    accentGlowClass: 'bg-emerald-400/20',
    chipClass: 'bg-slate-950 text-white',
    summary: 'Launching soon on the App Store with a refined mobile experience for reading, listening, and watching.',
  },
};

const AppComingSoonPage = () => {
  const { platform } = useParams<{ platform?: string }>();
  const { themeMode } = useShop();
  const isDark = themeMode === 'dark';
  const resolvedPlatform = platform === 'ios' ? 'ios' : 'android';
  const content = platformContent[resolvedPlatform];

  const badgeIcon = useMemo(
    () =>
      content.platform === 'android' ? (
        <GooglePlayBadgeIcon />
      ) : (
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 shadow-inner shadow-slate-200/70">
          <AppleBadgeIcon />
        </span>
      ),
    [content.platform],
  );

  const shellBackground = isDark
    ? resolvedPlatform === 'android'
      ? 'bg-[linear-gradient(135deg,#07111f_0%,#0b1730_38%,#06261e_100%)]'
      : 'bg-[linear-gradient(135deg,#08111d_0%,#0f1e3c_40%,#062822_100%)]'
    : 'bg-[linear-gradient(135deg,#dbe9ff_0%,#edf7ff_38%,#dff8f2_100%)]';

  const panelBackground = isDark
    ? resolvedPlatform === 'android'
      ? 'border-slate-700/80 bg-[linear-gradient(135deg,rgba(15,23,42,0.96)_0%,rgba(17,24,39,0.92)_45%,rgba(6,95,70,0.18)_100%)]'
      : 'border-slate-700/80 bg-[linear-gradient(135deg,rgba(15,23,42,0.96)_0%,rgba(12,26,46,0.94)_42%,rgba(4,120,87,0.16)_100%)]'
    : `border-white/70 bg-gradient-to-br ${content.accentClass}`;

  const frameCopyClass = isDark ? 'text-slate-200' : 'text-slate-950';
  const frameSubtleClass = isDark ? 'text-slate-400' : 'text-slate-600';
  const badgeSurfaceClass = isDark
    ? 'border-slate-600/80 bg-slate-900/80 text-slate-200'
    : 'border-white/80 bg-white/80 text-slate-700';
  const infoCardClass = isDark
    ? 'border-slate-700/80 bg-slate-900/70 shadow-[0_18px_40px_-30px_rgba(0,0,0,0.72)]'
    : 'border-white/80 bg-white/80 shadow-sm';
  const phoneShellClass = isDark ? 'bg-[#020617]' : 'bg-slate-950';
  const phoneScreenClass = isDark
    ? 'bg-[linear-gradient(180deg,#09111f_0%,#081421_52%,#081a18_100%)]'
    : 'bg-[linear-gradient(180deg,#ffffff_0%,#f6fbff_54%,#eefbf7_100%)]';
  const phoneDividerClass = isDark ? 'border-slate-800' : 'border-slate-200/80';
  const phoneCardClass = isDark ? 'border-slate-800 bg-slate-900 text-slate-100' : 'border-slate-200 bg-white text-slate-950';
  const statusCardClass = isDark ? 'border-emerald-900/70 bg-emerald-950/40' : 'border-emerald-200 bg-emerald-50';
  const releaseChipClass = isDark ? 'border-emerald-900/80 bg-emerald-950/50 text-emerald-300' : 'border-emerald-200 bg-emerald-50 text-emerald-700';

  return (
    <section className={`relative isolate overflow-hidden ${shellBackground} px-4 py-20 sm:px-6 lg:px-8`}>
      <div className="absolute inset-0 -z-10">
        <div className={`absolute -left-24 top-12 h-72 w-72 rounded-full blur-3xl ${isDark ? 'bg-orange-500/18' : content.accentGlowClass}`} />
        <div className={`absolute right-0 top-0 h-[28rem] w-[28rem] rounded-full blur-3xl ${isDark ? 'bg-sky-400/10' : 'bg-white/45'}`} />
        <div className={`absolute bottom-0 left-1/3 h-72 w-72 rounded-full blur-3xl ${isDark ? 'bg-emerald-400/10' : 'bg-cyan-300/15'}`} />
      </div>

      <div className="mx-auto max-w-7xl">
        <article className={`overflow-hidden rounded-[36px] border ${panelBackground} shadow-[0_35px_100px_-50px_rgba(15,23,42,0.45)] backdrop-blur-xl`}>
          <div className="grid gap-12 px-6 py-8 sm:px-8 sm:py-10 lg:grid-cols-[1.02fr_0.98fr] lg:px-12 lg:py-12">
            <div className="flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-4">
                  <img
                    src="/Screenshot_2026-02-18_120944-removebg-preview.png"
                    alt="Haapa Store"
                    className={`h-14 w-14 rounded-2xl border p-2 object-contain shadow-sm ${isDark ? 'border-slate-700 bg-slate-900/80' : 'border-white/70 bg-white/90'}`}
                  />
                  <div>
                    <p className={`text-2xl font-semibold tracking-tight ${frameCopyClass}`}>Haapa Store</p>
                    <p className={`text-sm ${frameSubtleClass}`}>Mobile experience preview</p>
                  </div>
                </div>

                <div className={`mt-10 inline-flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] shadow-sm ${badgeSurfaceClass}`}>
                  <Bell className="h-4 w-4 text-orange-500" />
                  Coming Soon
                </div>

                <h1 className={`mt-6 max-w-xl text-4xl font-semibold leading-tight sm:text-5xl ${frameCopyClass}`}>
                  {content.title}
                </h1>
                <p className={`mt-4 max-w-xl text-base leading-7 sm:text-lg ${frameSubtleClass}`}>
                  {content.summary}
                </p>

                <div className="mt-8 grid max-w-xl gap-4 sm:grid-cols-3">
                  <div className={`rounded-[24px] border px-5 py-5 ${infoCardClass}`}>
                    <p className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>Platform</p>
                    <p className={`mt-3 text-lg font-semibold ${frameCopyClass}`}>{content.storeLabel}</p>
                  </div>
                  <div className={`rounded-[24px] border px-5 py-5 ${infoCardClass}`}>
                    <p className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>Status</p>
                    <p className={`mt-3 text-lg font-semibold ${frameCopyClass}`}>Coming soon</p>
                  </div>
                  <div className={`rounded-[24px] border px-5 py-5 ${infoCardClass}`}>
                    <p className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>Experience</p>
                    <p className={`mt-3 text-lg font-semibold ${frameCopyClass}`}>Books, audio, video</p>
                  </div>
                </div>

                <div className={`mt-8 inline-flex items-center rounded-2xl border px-4 py-3 text-sm font-medium ${releaseChipClass}`}>
                  Release updates will appear here once the app is ready.
                </div>
              </div>
            </div>

            <div className="relative flex min-h-[420px] items-center justify-center lg:min-h-[560px]">
              <div className={`absolute inset-x-12 top-10 bottom-10 rounded-full blur-3xl ${isDark ? 'bg-white/8' : 'bg-white/30'}`} />

              <div className="relative w-full max-w-[360px]">
                <div className={`absolute -inset-8 rounded-full blur-3xl ${isDark ? 'bg-teal-300/10' : 'bg-teal-300/20'}`} />

                <div className={`relative mx-auto rounded-[42px] p-3 shadow-[0_40px_100px_-45px_rgba(15,23,42,0.72)] ${phoneShellClass}`}>
                  <div className={`absolute left-1/2 top-3 h-6 w-32 -translate-x-1/2 rounded-full ${isDark ? 'bg-slate-950' : 'bg-slate-900'}`} />

                  <div className={`overflow-hidden rounded-[34px] ${phoneScreenClass}`}>
                    <div className={`border-b px-5 pb-4 pt-8 ${phoneDividerClass}`}>
                      <div className="flex items-center justify-between">
                        <div className="inline-flex items-center gap-3">
                          <div className={`grid h-11 w-11 place-items-center rounded-2xl ${content.chipClass}`}>
                            {badgeIcon}
                          </div>
                          <div>
                            <p className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>{content.badgeLabel}</p>
                            <p className={`text-xl font-semibold ${frameCopyClass}`}>{content.storeLabel}</p>
                          </div>
                        </div>
                        <span className={`rounded-full px-3 py-1 text-[11px] font-semibold ${isDark ? 'bg-emerald-950/70 text-emerald-300' : 'bg-emerald-50 text-emerald-700'}`}>
                          Soon
                        </span>
                      </div>
                    </div>

                    <div className="space-y-4 px-5 py-5">
                      <div className="rounded-[28px] bg-slate-950 p-5 text-white">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/60">Haapa Mobile</p>
                            <p className="mt-2 text-xl font-semibold">All your stories in one place</p>
                          </div>
                          <div className="grid h-11 w-11 place-items-center rounded-2xl bg-white/10">
                            <Smartphone className="h-5 w-5" />
                          </div>
                        </div>

                        <div className="mt-5 h-2 rounded-full bg-white/15">
                          <div className="h-2 w-[62%] rounded-full bg-emerald-300" />
                        </div>
                        <p className="mt-3 text-sm text-white/72">Design and experience assembly in progress.</p>
                      </div>

                      <div className="grid gap-3">
                        <div className={`rounded-[24px] border px-4 py-4 shadow-sm ${phoneCardClass}`}>
                          <p className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>Reading</p>
                          <p className={`mt-2 text-base font-semibold ${frameCopyClass}`}>Offline library and bookmarks</p>
                        </div>
                        <div className={`rounded-[24px] border px-4 py-4 shadow-sm ${phoneCardClass}`}>
                          <p className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>Listening</p>
                          <p className={`mt-2 text-base font-semibold ${frameCopyClass}`}>Smooth audio playback and saved progress</p>
                        </div>
                        <div className={`rounded-[24px] border px-4 py-4 shadow-sm ${phoneCardClass}`}>
                          <p className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>Watching</p>
                          <p className={`mt-2 text-base font-semibold ${frameCopyClass}`}>Animation and kids mode in one app</p>
                        </div>
                      </div>

                      <div className={`rounded-[24px] border px-4 py-4 ${statusCardClass}`}>
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <p className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${isDark ? 'text-emerald-300' : 'text-emerald-700'}`}>Status</p>
                            <p className={`mt-2 text-base font-semibold ${frameCopyClass}`}>Mobile launch preparation underway</p>
                          </div>
                          <Bell className="h-5 w-5 text-orange-500" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </article>
      </div>
    </section>
  );
};

export default AppComingSoonPage;
